import { useEffect, useRef, useState, type PointerEvent } from "react";
import { Compass, Home, Layers3, Minus, Plus, Maximize2 } from "lucide-react";
import { ZONES, SYSTEM_INFO } from "../data/city";
import {
  mapGeometry,
  project,
  loadGeography,
  emptyGeometry,
} from "../lib/geography";
import { useCity } from "../state/CityContext";
import type { System } from "../simulation/types";
const connections: [number, number][] = [
  [0, 1],
  [1, 2],
  [1, 3],
  [3, 4],
  [3, 5],
  [4, 5],
  [2, 5],
];
export function CityMap() {
  const { state, dispatch, current } = useCity();
  const [tilt, setTilt] = useState(true),
    [zoom, setZoom] = useState(1),
    [centre, setCentre] = useState({ x: 960, y: 630 });
  const [size, setSize] = useState({ w: 1000, h: 720 });
  const [geoReady, setGeoReady] = useState(false),
    [geoError, setGeoError] = useState(false);
  const ref = useRef<SVGSVGElement>(null),
    pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef(false),
    moved = useRef(false);
  const geo = geoReady ? mapGeometry(tilt) : emptyGeometry;
  const scale = size.w < 600 ? 3.4 : 1.7;
  const viewWidth = (size.w * scale) / zoom,
    viewHeight = (size.h * scale) / zoom;
  const load = () => {
    setGeoError(false);
    loadGeography()
      .then(() => setGeoReady(true))
      .catch(() => setGeoError(true));
  };
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (!ref.current) return;
    const o = new ResizeObserver(([e]) =>
      setSize({ w: e.contentRect.width, h: e.contentRect.height }),
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  useEffect(() => {
    if (!state.selected) return;
    const p = project(
      ZONES.find((z) => z.id === state.selected)!.coordinates,
      tilt,
    );
    setCentre({ x: p[0], y: p[1] + 20 });
  }, [state.selected, tilt]);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((z) =>
        Math.max(0.6, Math.min(3, z * Math.exp(-e.deltaY * 0.0015))),
      );
    };
    node.addEventListener("wheel", wheel, { passive: false });
    return () => node.removeEventListener("wheel", wheel);
  }, []);
  const down = (e: PointerEvent<SVGSVGElement>) => {
    if ((e.target as Element).closest("[data-place]")) return;
    ref.current?.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    drag.current = true;
    moved.current = false;
  };
  const move = (e: PointerEvent<SVGSVGElement>) => {
    const old = pointers.current.get(e.pointerId);
    if (!old) return;
    const others = [...pointers.current.entries()].filter(
      ([id]) => id !== e.pointerId,
    );
    if (others.length) {
      const p = others[0][1];
      const before = Math.hypot(old.x - p.x, old.y - p.y),
        after = Math.hypot(e.clientX - p.x, e.clientY - p.y);
      if (before > 5)
        setZoom((z) => Math.max(0.6, Math.min(3, (z * after) / before)));
    } else {
      setCentre((c) => ({
        x: c.x - ((e.clientX - old.x) * viewWidth) / size.w,
        y: c.y - ((e.clientY - old.y) * viewHeight) / size.h,
      }));
    }
    if (Math.abs(e.clientX - old.x) + Math.abs(e.clientY - old.y) > 2)
      moved.current = true;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };
  const up = (e: PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    drag.current = pointers.current.size > 0;
  };
  const home = () => {
    setCentre({ x: 960, y: 630 });
    setZoom(1);
    dispatch({ type: "select", zone: null });
  };
  const networkColour =
    state.layer === "activity" ? "#d1eeab" : SYSTEM_INFO[state.layer].colour;
  const focus = state.layer === "activity" ? null : state.layer;
  const flowDuration = 3 + Math.max(0, 100 - current.mobility) / 10;
  return (
    <div
      className={`map-shell ${state.playing ? "is-running" : ""} layer-${state.layer}`}
    >
      <svg
        ref={ref}
        className="city-map"
        viewBox={`${centre.x - viewWidth / 2} ${centre.y - viewHeight / 2} ${viewWidth} ${viewHeight}`}
        aria-label="Interactive atlas of Lichfield. Drag to pan, pinch or scroll to zoom. Use the place list for keyboard navigation."
        role="group"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        <defs>
          <pattern
            id="city-grid"
            width="90"
            height="90"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(16)"
          >
            <path
              d="M90 0H0V90"
              fill="none"
              stroke="#375057"
              strokeWidth=".65"
              opacity=".3"
            />
            <circle r="1" fill="#536a6f" opacity=".3" />
          </pattern>
          <radialGradient id="flood">
            <stop offset="0%" stopColor="#7fc9e6" stopOpacity=".28" />
            <stop offset="80%" stopColor="#64b9d7" stopOpacity=".05" />
            <stop offset="100%" stopColor="#64b9d7" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="zone-halo">
            <stop offset="0%" stopColor={networkColour} stopOpacity=".14" />
            <stop offset="100%" stopColor={networkColour} stopOpacity="0" />
          </radialGradient>
          <filter id="small-glow" x="-70%" y="-70%" width="240%" height="240%">
            <feGaussianBlur stdDeviation="2" />
          </filter>
        </defs>
        <rect x="-2400" y="-1600" width="6400" height="4400" fill="#101c21" />
        <rect
          x="-2400"
          y="-1600"
          width="6400"
          height="4400"
          fill="url(#city-grid)"
        />
        <path d={geo.parks} fill="#1c3330" stroke="#2a4640" strokeWidth="1.2" />
        <path
          d={geo.paths}
          fill="none"
          stroke="#42605a"
          strokeWidth="1.1"
          strokeDasharray="3 3"
          opacity=".4"
        />
        <path
          d={geo.water}
          fill="#294754"
          stroke="#72adbf"
          strokeWidth="1.2"
          opacity=".85"
        />
        <path
          d={geo.streets}
          fill="none"
          stroke="#2c4048"
          strokeWidth="4"
          strokeLinejoin="round"
        />
        <path
          d={geo.arteries}
          fill="none"
          stroke="#526068"
          strokeWidth="8"
          strokeLinejoin="round"
        />
        <path
          d={geo.arteries}
          fill="none"
          stroke="#273c43"
          strokeWidth="5"
          strokeLinejoin="round"
        />
        <path d={geo.rails} fill="none" stroke="#728284" strokeWidth="2.7" />
        <path
          d={geo.rails}
          fill="none"
          stroke="#15272e"
          strokeWidth="1.1"
          strokeDasharray="6 6"
        />
        {geo.sides.map((d, i) => (
          <path
            key={`s${i}`}
            d={d}
            fill={["#1d2b31", "#27353b", "#202e34"][i]}
          />
        ))}
        {geo.roofs.map((d, i) => (
          <path
            key={`r${i}`}
            d={d}
            fill={["#38474d", "#3b4c51", "#34454c", "#405056", "#35494e"][i]}
            stroke="#51626a"
            strokeWidth=".5"
          />
        ))}
        <path
          d={geo.cathedral}
          fill="#7b8174"
          stroke="#c1c0a3"
          strokeWidth="1"
        />
        {tilt &&
          [
            [-1.8311, 52.68584],
            [-1.8311, 52.6856],
            [-1.83038, 52.68572],
          ].map((c, i) => {
            const p = project(c as [number, number], tilt);
            return (
              <g key={i}>
                <path
                  d={`M${p[0] - 5} ${p[1] - 18}L${p[0]} ${p[1] - 64}L${p[0] + 5} ${p[1] - 18}Z`}
                  fill="#b1b194"
                  stroke="#c9c6a7"
                  strokeWidth=".6"
                />
                <path
                  d={`M${p[0]} ${p[1] - 64}L${p[0] + 5} ${p[1] - 18}L${p[0]} ${p[1] - 22}Z`}
                  fill="#717d75"
                />
              </g>
            );
          })}
        {geo.waterLabels.map((w, i) => (
          <text
            key={i}
            x={w.point[0]}
            y={w.point[1]}
            textAnchor="middle"
            fill="#93bfcd"
            fontSize="12"
            fontStyle="italic"
            letterSpacing="1.5"
            className="map-label"
          >
            {w.name.toUpperCase()}
          </text>
        ))}
        {(state.layer === "water" ||
          (state.layer === "activity" &&
            current.zones.some((z) => z.water > 20))) &&
          current.zones.map((s, i) => {
            const p = project(ZONES[i].coordinates, tilt);
            return s.water > 8 ? (
              <g key={s.id} className="water-field">
                <ellipse
                  cx={p[0]}
                  cy={p[1]}
                  rx={s.water * 2.1 + 25}
                  ry={s.water * 1.3 + 15}
                  fill="url(#flood)"
                />
                <ellipse
                  cx={p[0]}
                  cy={p[1]}
                  rx={s.water * 1.4 + 20}
                  ry={s.water * 0.85 + 12}
                  fill="none"
                  stroke="#83c4df"
                  opacity=".35"
                  strokeWidth="1"
                  strokeDasharray="5 5"
                />
              </g>
            ) : null;
          })}
        {state.layer !== "water" &&
          connections.map(([a, b], i) => {
            const p = project(ZONES[a].coordinates, tilt),
              q = project(ZONES[b].coordinates, tilt);
            const d = `M${p[0]} ${p[1]} Q${(p[0] + q[0]) / 2} ${(p[1] + q[1]) / 2 - 45} ${q[0]} ${q[1]}`;
            const stress = focus
              ? (current.zones[a][focus] + current.zones[b][focus]) / 2
              : (200 -
                  current.zones[a].continuity -
                  current.zones[b].continuity) /
                2;
            return (
              <g key={i} className="network-link">
                <path
                  d={d}
                  fill="none"
                  stroke={stress > 55 ? "#e0a18b" : networkColour}
                  strokeWidth="1.4"
                  opacity={state.layer === "activity" ? 0.25 : 0.65}
                  strokeDasharray={state.layer === "power" ? "6 4" : undefined}
                />
                <path
                  d={d}
                  fill="none"
                  stroke={networkColour}
                  strokeWidth="3"
                  strokeDasharray="2 42"
                  className="network-flow"
                  style={{
                    animationDuration: `${flowDuration}s`,
                    animationPlayState: state.playing ? "running" : "paused",
                  }}
                  opacity=".9"
                />
              </g>
            );
          })}
        {ZONES.map((z, i) => {
          const world = project(z.coordinates, tilt),
            p = [0, 0],
            s = current.zones[i],
            severity = focus ? s[focus] : 100 - s.continuity,
            selected = state.selected === z.id;
          const colour = severity > 50 ? "#e7a187" : networkColour;
          return (
            <g
              key={z.id}
              transform={`translate(${world[0]} ${world[1]}) scale(${scale / zoom})`}
              data-place={z.id}
              role="button"
              tabIndex={0}
              aria-label={`Explore ${z.name}. Continuity ${Math.round(s.continuity)} per cent.`}
              aria-pressed={selected}
              className={`map-place ${selected ? "selected" : ""}`}
              onClick={() => {
                if (!drag.current) dispatch({ type: "select", zone: z.id });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  dispatch({ type: "select", zone: z.id });
                }
              }}
            >
              <circle
                cx={p[0]}
                cy={p[1]}
                r={selected ? 115 : 70}
                fill="url(#zone-halo)"
                pointerEvents="none"
              />
              <circle cx={p[0]} cy={p[1]} r="24" fill="transparent" />
              <circle
                cx={p[0]}
                cy={p[1]}
                r={selected ? 15 : 11}
                fill="#10222a"
                stroke={colour}
                strokeWidth={selected ? 2 : 1.5}
              />
              <circle cx={p[0]} cy={p[1]} r="4" fill={colour} />
              {severity > 25 && (
                <circle
                  cx={p[0]}
                  cy={p[1]}
                  r="19"
                  fill="none"
                  stroke={colour}
                  strokeWidth="1"
                  strokeDasharray="2 5"
                  className="stress-ring"
                />
              )}
              <rect
                x={p[0] + 17}
                y={p[1] - 15}
                rx="4"
                width={z.short.length * 6.3 + 18}
                height="27"
                fill="#0c181e"
                opacity=".92"
              />
              <text
                x={p[0] + 25}
                y={p[1] + 2}
                fill={selected ? "#e5f4d9" : "#c8d5d6"}
                fontSize="11"
                fontWeight="600"
                className="map-label"
              >
                {z.short}
              </text>
              {state.mode !== "explore" && (
                <text
                  x={p[0] + 24}
                  y={p[1] + 25}
                  fill={colour}
                  fontSize="8"
                  className="map-label"
                >
                  {Math.round(severity)}% {focus ? "STRESS" : "IMPACT"}
                </text>
              )}
            </g>
          );
        })}
        <text
          x="1540"
          y="975"
          fill="#4c696a"
          fontSize="24"
          letterSpacing="10"
          transform="rotate(16 1540 975)"
          pointerEvents="none"
        >
          LICHFIELD
        </text>
      </svg>
      <div className="map-atmosphere" aria-hidden="true" />
      {!geoReady && (
        <div className="geography-loading" role="status">
          {geoError ? (
            <>
              <span>
                Map data is unavailable. The simulation remains usable.
              </span>
              <button className="text-button" onClick={load}>
                Retry map download
              </button>
            </>
          ) : (
            <span>Preparing the Lichfield atlas…</span>
          )}
        </div>
      )}
      <div className="map-controls">
        <button
          title="Zoom in"
          aria-label="Zoom in"
          onClick={() => setZoom((z) => Math.min(3, z * 1.2))}
        >
          <Plus size={17} />
        </button>
        <button
          title="Zoom out"
          aria-label="Zoom out"
          onClick={() => setZoom((z) => Math.max(0.6, z / 1.2))}
        >
          <Minus size={17} />
        </button>
        <span />
        <button
          title="Return to city overview"
          aria-label="Return to city overview"
          onClick={home}
        >
          <Home size={16} />
        </button>
        <button
          title="Toggle map perspective"
          aria-label="Toggle map perspective"
          aria-pressed={tilt}
          onClick={() => setTilt((t) => !t)}
        >
          {tilt ? <Layers3 size={17} /> : <Maximize2 size={17} />}
        </button>
      </div>
      <div className="compass" title="North direction">
        <Compass
          size={25}
          style={{ transform: tilt ? "rotate(-20deg)" : undefined }}
        />
        <span>N</span>
      </div>
      <div className="map-credit">
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
        >
          © OpenStreetMap contributors
        </a>
        <span>Illustrative heights · Synthetic overlays</span>
      </div>
      <div className="map-scale">
        <i style={{ width: `${(100 / scale) * zoom}px` }} />
        <span>≈ 200 m</span>
      </div>
    </div>
  );
}
